import { create, tsx } from '@dojo/framework/core/vdom';
import { Head } from '../../widgets/Head';
import { Hero } from '../../widgets/Hero';
import { Image } from '../../widgets/Image';
import { ImageSection } from '../../widgets/ImageSection';
import { Technologies } from '../../widgets/Technologies';
import { Text } from '../../widgets/Text';

import * as css from './CaseStudy.m.css';
import * as commonCss from '../../Common.m.css';
import * as summaryCss from '../../widgets/Summary.m.css';
import ContentWrapper from '../../widgets/ContentWrapper';
import Section from '../../widgets/Section';
import SocialCaseStudyList, { rvCompleteBlurb } from './SocialCaseStudyList';

const skRvCompleteMockup = require('../../assets/images/work-mockups/sk-rv-complete-mockup.png');
const skRvCompleteMockupHighRes = require('../../assets/images/work-mockups/sk-rv-complete-mockup@2x.png');

const factory = create();

export const CaseStudySocialRVComplete = factory(function CaseStudySocialRVComplete() {
	return (
		<div classes={css.root}>
			<Head
				title="Online Community Platform App Development Case Study | SitePen"
				description="SitePen developed a customizable mobile application for Social Knowledge which greatly improved the development time required to add new features."
				noindex
			/>

			<Hero background="level1BluePurple" align="center" paddingBottom="sm">
				{{
					title: 'Case Study',
					xlTitle: 'Social Knowledge',
					subtitle: 'Project: RV Complete',
					blurb: (
						<virtual>
							<span>{rvCompleteBlurb}</span>
							<Technologies
								icons={[
									'react',
									'react-native',
									'redux',
									'mobx',
									'typescript',
									'jest'
								]}
							/>
						</virtual>
					)
				}}
			</Hero>

			<Section type="leading">
				<ContentWrapper size="small">
					<Text type="section">The Situation</Text>
					<Text type="body">
						Social Knowledge creates RV Life, a platform to make camping simple. Social
						Knowledge approached SitePen to help offer a mobile application for their RV
						retail partners, offering a subset of RV Life features within the same
						platform as a whitelabel product experience.
					</Text>
					<Text type="body">
						Key functionality includes managing user profiles, providing roadside
						assistance to their users, and utilizing mapping technology to serve rich
						data and reviews to their users.
					</Text>
				</ContentWrapper>
			</Section>

			<Section shaded>
				<ImageSection contentBias>
					{{
						title: (
							<div classes={[css.stackBlurbNoGutter]}>
								<Text type="section">The Solution</Text>
							</div>
						),
						blurb: (
							<div>
								<Text type="body">
									SitePen created a lightweight mobile application for Social
									Knowledge using the React Native framework. This allowed SitePen
									to develop iOS and Android apps simultaneously using the same
									maintainable codebase with web technologies. Changes get made to
									both app derivatives simultaneously, providing a much faster
									feature rollout time compared to separate native app code bases.
								</Text>
								<Text type="body">
									SitePen also selected the Expo platform as a starting point for
									the RV Retailer application. It provides a streamlined process
									for development and for deploying to devices for testing via the
									expo client. Development speed for this application was critical
									to Social Knowledge as it is to most organizations! Expo allows
									easy over-the-air updates to applications, providing new
									features and bug fixes to test users much quicker than
									traditional approaches via app stores.
								</Text>
								<Text type="body">
									Working with TypeScript allowed SitePen to quickly and
									efficiently collaborate with a distributed team allowing for
									interfaces and the Typescript type system ensures compatibility
									between components and APIs.
								</Text>
								<Text type="body">
									Jest was selected for the testing framework due to its
									compatibility with React Native. Jest allows developers to write
									unit rests that exercise component APIs and snapshot tests,
									ensuring components get rendered as expected. Combined these
									tests are both efficient to create and maintain, and also vastly
									improve product confidence when making changes, minimizing
									downtime caused by future mistakes.
								</Text>
								<Text type="body">
									Social Knowledge expressed an interest in adding end-to-end
									tests to the application so SitePen set up an approach utilizing
									Cavy as the test runner. Cavy provides compatibility with the
									Expo platform and both Android and iOS out of the box. This
									enabled us to deliver functional tests along with the
									application with full OS coverage and allows Social Knowledge to
									continue adding tests as application functionality grows in the
									future.
								</Text>
							</div>
						),
						images: {
							main: (
								<Image
									src={skRvCompleteMockup}
									highResSrc={skRvCompleteMockupHighRes}
									shadow="light"
								/>
							)
						}
					}}
				</ImageSection>
			</Section>

			<Section>
				<ContentWrapper>
					<Text type="section">The Result</Text>
					<p classes={[commonCss.paragraphWidth, summaryCss.blurb]}>
						In one big step, SitePen eliminated the weighty technical debt of an
						outdated front-end technology stack and helped put Social Knowledge on a
						sustainable path with better development possibilities in their new React
						environment.
					</p>
				</ContentWrapper>
			</Section>
			<Section>
				<ContentWrapper>
					<SocialCaseStudyList caseStudies={['trip', 'life']} />
				</ContentWrapper>
			</Section>
		</div>
	);
});

export default CaseStudySocialRVComplete;
